import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, Download } from 'lucide-react';
import ReactPDFViewer from '@/components/documents/react-pdf-viewer';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';


interface TextHighlight {
  text: string;
  pageNumber: number;
  reviewItemId?: string;
  reviewItemIndex?: number;
  judgment?: 'valid' | 'invalid' | 'excessive' | 'unclear';
  reviewItemTitle?: string;
}

interface DocumentWithHighlights {
  documentId: string;
  documentName: string;
  documentUrl: string;
  textHighlights?: TextHighlight[];
  targetPage?: number;
}

interface ContractReviewDocumentViewerProps {
  claimDocuments: DocumentWithHighlights[];
  contractDocuments: DocumentWithHighlights[];
  activeClaimDocumentId?: string;
  activeContractDocumentId?: string;
  autoScrollToHighlight?: boolean;
  targetHighlightId?: string;
  onDocumentSelect: (documentId: string, documentType: 'claim' | 'contract') => void;
}

export default function ContractReviewDocumentViewer({
  claimDocuments,
  contractDocuments,
  activeClaimDocumentId,
  activeContractDocumentId,
  autoScrollToHighlight = false,
  targetHighlightId,
  onDocumentSelect,
}: ContractReviewDocumentViewerProps) {
  const [claimAccordionValue, setClaimAccordionValue] = useState<string>("");
  const [contractAccordionValue, setContractAccordionValue] = useState<string>("");
  const { toast } = useToast();

  // Update accordion values when documents are loaded or active documents change
  React.useEffect(() => {
    if (activeClaimDocumentId) {
      setClaimAccordionValue(activeClaimDocumentId);
    } else if (claimDocuments.length > 0 && (!claimAccordionValue || claimAccordionValue === "")) {
      // Set first document as default when documents load
      setClaimAccordionValue(claimDocuments[0].documentId);
    }
  }, [activeClaimDocumentId, claimDocuments]);

  React.useEffect(() => {
    if (activeContractDocumentId) {
      setContractAccordionValue(activeContractDocumentId);
    } else if (contractDocuments.length > 0 && (!contractAccordionValue || contractAccordionValue === "")) {
      // Set first document as default when documents load
      setContractAccordionValue(contractDocuments[0].documentId);
    }
  }, [activeContractDocumentId, contractDocuments]);

  const handleClaimAccordionChange = (value: string) => {
    setClaimAccordionValue(value);
    if (value) {
      onDocumentSelect(value, 'claim');
    }
  };

  const handleContractAccordionChange = (value: string) => {
    setContractAccordionValue(value);
    if (value) {
      onDocumentSelect(value, 'contract');
    }
  };

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    if (extension === 'pdf') {
      return <FileText className="h-4 w-4 text-red-500" />;
    }
    return <FileText className="h-4 w-4 text-neutral-500" />;
  };

  const getHighlightBadge = (doc: DocumentWithHighlights) => {
    const highlightCount = doc.textHighlights?.length || 0;
    if (highlightCount > 0) {
      return (
        <Badge variant="secondary" className="ml-2 text-xs bg-yellow-100 text-yellow-700">
          📍 {highlightCount}
        </Badge>
      );
    }
    return null;
  };

  const handleDownload = async (documentUrl: string, documentName: string) => {
    try {
      // First fetch the download URL from the API with proper authentication
      const response = await apiRequest("GET", documentUrl);
      const data = await response.json();
      
      // Open the signed URL in a new tab
      window.open(data.downloadUrl, '_blank');
      
      toast({
        title: "Download started",
        description: `Downloading ${documentName}`,
      });
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: "Download failed",
        description: error instanceof Error ? error.message : "Failed to download document",
        variant: "destructive",
      });
    }
  };

  const handleHighlightClick = (highlight: TextHighlight, documentId: string, documentType: 'claim' | 'contract') => {
    console.log('Highlight clicked:', { highlight, documentId, documentType });
    // You can add specific highlight click handling logic here
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Claim Documents Column */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center">
              <FileText className="h-5 w-5 mr-2 text-blue-600" />
              Claim Documents ({claimDocuments.length})
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {claimDocuments.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">
              No claim documents available
            </p>
          ) : (
            <Accordion 
              type="single" 
              collapsible 
              value={claimAccordionValue}
              onValueChange={handleClaimAccordionChange}
            >
              {claimDocuments.map((doc) => (
                <AccordionItem key={doc.documentId} value={doc.documentId}>
                  <div className="flex items-center">
                    <AccordionTrigger className="flex-1 hover:no-underline">
                      <div className="flex items-center w-full">
                        {getFileIcon(doc.documentName)}
                        <span className="ml-2 text-sm font-medium truncate max-w-[200px]" title={doc.documentName}>
                          {doc.documentName}
                        </span>
                        {getHighlightBadge(doc)}
                      </div>
                    </AccordionTrigger>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-2 shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(doc.documentUrl, doc.documentName);
                      }}
                    >
                      <Download className="h-3 w-3" />
                    </Button>
                  </div>
                  <AccordionContent>
                    <ReactPDFViewer
                      documentUrl={doc.documentUrl}
                      documentName={doc.documentName}
                      textHighlights={doc.textHighlights || []}
                      activePageNumber={doc.targetPage || 1}
                      autoScrollToHighlight={autoScrollToHighlight && doc.documentId === activeClaimDocumentId}
                      targetHighlightId={targetHighlightId}
                      maxHeight="500px"
                      onHighlightClick={(highlight) => handleHighlightClick(highlight, doc.documentId, 'claim')}
                    />
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </CardContent>
      </Card>

      {/* Contract Documents Column */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center">
              <FileText className="h-5 w-5 mr-2 text-green-600" />
              Contract Documents ({contractDocuments.length})
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {contractDocuments.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">
              No contract documents available
            </p>
          ) : (
            <Accordion 
              type="single" 
              collapsible 
              value={contractAccordionValue}
              onValueChange={handleContractAccordionChange}
            >
              {contractDocuments.map((doc) => (
                <AccordionItem key={doc.documentId} value={doc.documentId}>
                  <div className="flex items-center">
                    <AccordionTrigger className="flex-1 hover:no-underline">
                      <div className="flex items-center w-full">
                        {getFileIcon(doc.documentName)}
                        <span className="ml-2 text-sm font-medium truncate max-w-[200px]" title={doc.documentName}>
                          {doc.documentName}
                        </span>
                        {getHighlightBadge(doc)}
                      </div>
                    </AccordionTrigger>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-2 shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(doc.documentUrl, doc.documentName);
                      }}
                    >
                      <Download className="h-3 w-3" />
                    </Button>
                  </div>
                  <AccordionContent>
                    <ReactPDFViewer
                      documentUrl={doc.documentUrl}
                      documentName={doc.documentName}
                      textHighlights={doc.textHighlights || []}
                      activePageNumber={doc.targetPage || 1}
                      autoScrollToHighlight={autoScrollToHighlight && doc.documentId === activeContractDocumentId}
                      targetHighlightId={targetHighlightId}
                      maxHeight="500px"
                      onHighlightClick={(highlight) => handleHighlightClick(highlight, doc.documentId, 'contract')}
                    />
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </CardContent>
      </Card>
    </div>
  );
} 